import React, { useState, useEffect } from 'react';
import {
 Box,
 Card,
 CardContent,
 Typography,
 Button,
 Chip,
 List,
 ListItem,
 ListItemText,
 ListItemSecondaryAction,
 IconButton,
 Dialog,
 DialogTitle,
 DialogContent,
 DialogActions,
 TextField,
 Grid,
 Avatar,
 Badge,
 Divider,
 Paper,
 Tooltip,
 Stepper,
 Step,
 StepLabel,
 StepContent,
 Accordion,
 AccordionSummary,
 AccordionDetails,
 LinearProgress
} from '@mui/material';
import {
 Person,
 Assignment,
 Chat,
 CheckCircle,
 Pending,
 PlayArrow,
 Stop,
 Refresh,
 Message,
 Group,
 ExpandMore,
 Timeline,
 Code,
 BugReport,
 Palette,
 Storage
} from '@mui/icons-material';
import './TeamRoleReplica.css';

const TeamRoleReplica = () => {
 const [selectedProject, setSelectedProject] = useState(null);
 const [teamMembers, setTeamMembers] = useState([]);
 const [chatOpen, setChatOpen] = useState(false);
 const [selectedPhase, setSelectedPhase] = useState(null);
 const [chatMessages, setChatMessages] = useState([]);
 const [newMessage, setNewMessage] = useState('');
 const [activeUsers, setActiveUsers] = useState(new Set());

 // Mock project data with team structure
 const mockProject = {
 id: 'project1',
 title: 'Blog Platform with Authentication',
 description: 'A comprehensive blog platform with secure authentication, user management, and content creation features.',
 totalPhases: 50,
 currentPhase: 3,
 teamMembers: [
 {
 id: 'person1',
 name: 'Alex Johnson',
 role: 'Frontend Developer',
 avatar: 'A',
 color: '#4CAF50',
 responsibilities: ['UI Design', 'Component Development', 'User Experience'],
 icon: <Palette />,
 tasks: [
 {
 id: 'task1',
 title: 'Analyze authentication requirements and design',
 description: 'Gather and analyze requirements for the authentication system, design the authentication flow (e.g., JWT or OAuth), and create technical specifications.',
 phase: 1,
 status: 'completed',
 priority: 'high',
 estimatedHours: 8,
 actualHours: 7
 },
 {
 id: 'task2',
 title: 'Analyze database requirements and design schema',
 description: 'Analyze requirements for blog post storage and design a normalized SQL or NoSQL schema, including tables/collections for posts and metadata.',
 phase: 2,
 status: 'in_progress',
 priority: 'medium',
 estimatedHours: 12,
 actualHours: 8
 },
 {
 id: 'task3',
 title: 'Analyze UI requirements and design',
 description: 'Gather requirements for the blog post creation UI and design wireframes or mockups for the interface, focusing on usability.',
 phase: 3,
 status: 'pending',
 priority: 'low',
 estimatedHours: 6,
 actualHours: 0
 }
 ]
 },
 {
 id: 'person2',
 name: 'Sarah Chen',
 role: 'Backend Developer',
 avatar: 'S',
 color: '#2196F3',
 responsibilities: ['API Development', 'Database Design', 'Authentication'],
 icon: <Storage />,
 tasks: [
 {
 id: 'task4',
 title: 'Develop backend authentication APIs',
 description: 'Implement backend APIs for user registration, login, and token management, including database integration for storing user credentials securely.',
 phase: 1,
 status: 'completed',
 priority: 'high',
 estimatedHours: 16,
 actualHours: 18
 },
 {
 id: 'task5',
 title: 'Implement database schema and queries',
 description: 'Create the database schema in the chosen database system (e.g., PostgreSQL or MongoDB) and develop queries for CRUD operations.',
 phase: 2,
 status: 'in_progress',
 priority: 'high',
 estimatedHours: 10,
 actualHours: 6
 },
 {
 id: 'task6',
 title: 'Develop backend APIs for post creation',
 description: 'Implement backend APIs to handle blog post creation and editing, integrating with the database schema for storage.',
 phase: 3,
 status: 'pending',
 priority: 'medium',
 estimatedHours: 14,
 actualHours: 0
 }
 ]
 },
 {
 id: 'person3',
 name: 'Mike Rodriguez',
 role: 'UI/UX Developer',
 avatar: 'M',
 color: '#FF9800',
 responsibilities: ['Frontend Components', 'Responsive Design', 'User Interface'],
 icon: <Code />,
 tasks: [
 {
 id: 'task7',
 title: 'Create frontend authentication UI',
 description: 'Develop React-based UI components for user registration, login, and password management, ensuring a responsive and user-friendly interface.',
 phase: 1,
 status: 'completed',
 priority: 'medium',
 estimatedHours: 12,
 actualHours: 14
 },
 {
 id: 'task8',
 title: 'Develop schema management interface',
 description: 'Create a basic admin interface for viewing and managing the blog post schema, ensuring accessibility for future updates.',
 phase: 2,
 status: 'pending',
 priority: 'low',
 estimatedHours: 8,
 actualHours: 0
 },
 {
 id: 'task9',
 title: 'Build frontend post creation UI',
 description: 'Develop React components for the blog post creation and editing interface, supporting text input and basic formatting.',
 phase: 3,
 status: 'pending',
 priority: 'medium',
 estimatedHours: 16,
 actualHours: 0
 }
 ]
 },
 {
 id: 'person4',
 name: 'Emily Watson',
 role: 'QA/Testing Engineer',
 avatar: 'E',
 color: '#9C27B0',
 responsibilities: ['Testing', 'Quality Assurance', 'Documentation'],
 icon: <BugReport />,
 tasks: [
 {
 id: 'task10',
 title: 'Test and integrate authentication',
 description: 'Write automated tests (unit and integration) for the authentication system and ensure seamless integration between frontend and backend components.',
 phase: 1,
 status: 'in_progress',
 priority: 'high',
 estimatedHours: 10,
 actualHours: 6
 },
 {
 id: 'task11',
 title: 'Test schema and document',
 description: 'Write automated tests for the database schema and queries, and produce documentation detailing the schema structure and usage.',
 phase: 2,
 status: 'pending',
 priority: 'medium',
 estimatedHours: 8,
 actualHours: 0
 },
 {
 id: 'task12',
 title: 'Test UI and integration',
 description: 'Write automated tests for the post creation UI and APIs, ensuring seamless integration and functionality across components.',
 phase: 3,
 status: 'pending',
 priority: 'high',
 estimatedHours: 12,
 actualHours: 0
 }
 ]
 }
 ],
 phases: [
 {
 phase: 1,
 title: 'Authentication System',
 description: 'Implement secure user authentication system',
 status: 'completed',
 tasks: [
 { memberId: 'person1', taskId: 'task1', status: 'completed' },
 { memberId: 'person2', taskId: 'task4', status: 'completed' },
 { memberId: 'person3', taskId: 'task7', status: 'completed' },
 { memberId: 'person4', taskId: 'task10', status: 'in_progress' }
 ]
 },
 {
 phase: 2,
 title: 'Database Schema',
 description: 'Design and implement database schema for blog posts',
 status: 'in_progress',
 tasks: [
 { memberId: 'person1', taskId: 'task2', status: 'in_progress' },
 { memberId: 'person2', taskId: 'task5', status: 'in_progress' },
 { memberId: 'person3', taskId: 'task8', status: 'pending' },
 { memberId: 'person4', taskId: 'task11', status: 'pending' }
 ]
 },
 {
 phase: 3,
 title: 'Blog Post Creation',
 description: 'Develop blog post creation and editing functionality',
 status: 'pending',
 tasks: [
 { memberId: 'person1', taskId: 'task3', status: 'pending' },
 { memberId: 'person2', taskId: 'task6', status: 'pending' },
 { memberId: 'person3', taskId: 'task9', status: 'pending' },
 { memberId: 'person4', taskId: 'task12', status: 'pending' }
 ]
 }
 ]
 };

 // Mock chat messages
 const mockChatMessages = [
 {
 id: 1,
 senderId: 'person1',
 senderName: 'Alex Johnson',
 message: 'Hey team! I\'ve completed the authentication design. The wireframes are ready for review.',
 timestamp: new Date(Date.now() - 1000 * 60 * 30),
 type: 'text'
 },
 {
 id: 2,
 senderId: 'person2',
 senderName: 'Sarah Chen',
 message: 'Great work Alex! I\'m working on the backend APIs now. Should be ready by EOD.',
 timestamp: new Date(Date.now() - 1000 * 60 * 25),
 type: 'text'
 },
 {
 id: 3,
 senderId: 'person4',
 senderName: 'Emily Watson',
 message: 'I can start testing the auth flow once Sarah completes the APIs. What\'s the timeline?',
 timestamp: new Date(Date.now() - 1000 * 60 * 20),
 type: 'text'
 },
 {
 id: 4,
 senderId: 'person3',
 senderName: 'Mike Rodriguez',
 message: 'I\'ll need the API endpoints to start building the frontend components. Can you share the API docs?',
 timestamp: new Date(Date.now() - 1000 * 60 * 15),
 type: 'text'
 },
 {
 id: 5,
 senderId: 'person2',
 senderName: 'Sarah Chen',
 message: 'Sure! I\'ll create the API documentation and share it in the next hour.',
 timestamp: new Date(Date.now() - 1000 * 60 * 10),
 type: 'text'
 }
 ];

 useEffect(() => {
 setSelectedProject(mockProject);
 setTeamMembers(mockProject.teamMembers);
 setChatMessages(mockChatMessages);
 setActiveUsers(new Set(['person1', 'person2', 'person3', 'person4']));
 }, []);

 const getStatusColor = (status) => {
 switch (status) {
 case 'completed': return '#4CAF50';
 case 'in_progress': return '#FF9800';
 case 'pending': return '#9E9E9E';
 default: return '#9E9E9E';
 }
 };

 const getStatusIcon = (status) => {
 switch (status) {
 case 'completed': return <CheckCircle />;
 case 'in_progress': return <PlayArrow />;
 case 'pending': return <Pending />;
 default: return <Pending />;
 }
 };

 const getPriorityColor = (priority) => {
 switch (priority) {
 case 'high': return '#F44336';
 case 'medium': return '#FF9800';
 case 'low': return '#4CAF50';
 default: return '#9E9E9E';
 }
 };

 const calculatePhaseProgress = (phase) => {
 const completedTasks = phase.tasks.filter(task => {
 const member = teamMembers.find(m => m.id === task.memberId);
 const memberTask = member?.tasks.find(t => t.id === task.taskId);
 return memberTask?.status === 'completed';
 }).length;
 return (completedTasks / phase.tasks.length) * 100;
 };

 const calculateOverallProgress = () => {
 const totalTasks = teamMembers.reduce((sum, member) => sum + member.tasks.length, 0);
 const completedTasks = teamMembers.reduce((sum, member) => 
 sum + member.tasks.filter(task => task.status === 'completed').length, 0);
 return (completedTasks / totalTasks) * 100;
 };

 const handleSendMessage = () => {
 if (newMessage.trim()) {
 const message = {
 id: chatMessages.length + 1,
 senderId: 'person1',
 senderName: 'Alex Johnson',
 message: newMessage.trim(),
 timestamp: new Date(),
 type: 'text'
 };
 setChatMessages(prev => [...prev, message]);
 setNewMessage('');
 }
 };

 const formatTime = (timestamp) => {
 return timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
 };

 const getOnlineStatus = (userId) => {
 return activeUsers.has(userId);
 };

 return (
 <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
 <Typography variant="h4" gutterBottom sx={{ color: '#333', fontWeight: 'bold' }}>
 Team Role Replica - {selectedProject?.title}
 </Typography>

 {/* Project Overview */}
 <Card sx={{ mb: 3 }}>
 <CardContent>
 <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
 <Typography variant="h5" component="div">
 Project Overview
 </Typography>
 <Button 
 variant="contained" 
 startIcon={<Chat />}
 onClick={() => setChatOpen(true)}
 >
 Team Chat
 </Button>
 </Box>
 
 <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
 {selectedProject?.description}
 </Typography>
 
 <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
 <Typography variant="body2">
 Overall Progress: {Math.round(calculateOverallProgress())}%
 </Typography>
 <LinearProgress 
 variant="determinate" 
 value={calculateOverallProgress()} 
 sx={{ flex: 1, height: 8, borderRadius: 4 }}
 />
 </Box>
 
 <Grid container spacing={2}>
 <Grid item xs={12} sm={3}>
 <Box sx={{ textAlign: 'center' }}>
 <Typography variant="h6" color="primary">
 {selectedProject?.totalPhases}
 </Typography>
 <Typography variant="body2" color="text.secondary">
 Total Phases
 </Typography>
 </Box>
 </Grid>
 <Grid item xs={12} sm={3}>
 <Box sx={{ textAlign: 'center' }}>
 <Typography variant="h6" color="primary">
 {selectedProject?.currentPhase}
 </Typography>
 <Typography variant="body2" color="text.secondary">
 Current Phase
 </Typography>
 </Box>
 </Grid>
 <Grid item xs={12} sm={3}>
 <Box sx={{ textAlign: 'center' }}>
 <Typography variant="h6" color="primary">
 {teamMembers.length}
 </Typography>
 <Typography variant="body2" color="text.secondary">
 Team Members
 </Typography>
 </Box>
 </Grid>
 <Grid item xs={12} sm={3}>
 <Box sx={{ textAlign: 'center' }}>
 <Typography variant="h6" color="primary">
 {teamMembers.reduce((sum, member) => sum + member.tasks.length, 0)}
 </Typography>
 <Typography variant="body2" color="text.secondary">
 Total Tasks
 </Typography>
 </Box>
 </Grid>
 </Grid>
 </CardContent>
 </Card>

 <Grid container spacing={3}>
 {/* Project Phases */}
 <Grid item xs={12} lg={8}>
 <Card>
 <CardContent>
 <Typography variant="h6" gutterBottom>
 Project Phases
 </Typography>
 
 {selectedProject?.phases.map((phase) => (
 <Accordion key={phase.phase} expanded={selectedPhase === phase.phase} onChange={() => setSelectedPhase(selectedPhase === phase.phase ? null : phase.phase)}>
 <AccordionSummary expandIcon={<ExpandMore />}>
 <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
 <Typography variant="h6">
 Phase {phase.phase}: {phase.title}
 </Typography>
 <Chip 
 label={phase.status} 
 size="small"
 sx={{ 
 bgcolor: getStatusColor(phase.status),
 color: 'white'
 }}
 />
 <Box sx={{ flex: 1 }}>
 <LinearProgress 
 variant="determinate" 
 value={calculatePhaseProgress(phase)} 
 sx={{ height: 6, borderRadius: 3 }}
 />
 </Box>
 <Typography variant="body2" color="text.secondary">
 {Math.round(calculatePhaseProgress(phase))}%
 </Typography>
 </Box>
 </AccordionSummary>
 <AccordionDetails>
 <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
 {phase.description}
 </Typography>
 
 <Grid container spacing={2}>
 {phase.tasks.map((task) => {
 const member = teamMembers.find(m => m.id === task.memberId);
 const memberTask = member?.tasks.find(t => t.id === task.taskId);
 return (
 <Grid item xs={12} md={6} key={task.taskId}>
 <Card variant="outlined" sx={{ p: 2 }}>
 <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
 <Avatar sx={{ bgcolor: member?.color, width: 32, height: 32, mr: 1 }}>
 {member?.avatar}
 </Avatar>
 <Typography variant="subtitle2">
 {member?.name}
 </Typography>
 </Box>
 <Typography variant="body2" sx={{ mb: 1, fontWeight: 'medium' }}>
 {memberTask?.title}
 </Typography>
 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
 <Chip 
 icon={getStatusIcon(memberTask?.status)}
 label={memberTask?.status?.replace('_', ' ')}
 size="small"
 sx={{ 
 bgcolor: getStatusColor(memberTask?.status),
 color: 'white',
 fontSize: '0.7rem'
 }}
 />
 <Typography variant="caption" color="text.secondary">
 {memberTask?.actualHours}/{memberTask?.estimatedHours}h
 </Typography>
 </Box>
 </Card>
 </Grid>
 );
 })}
 </Grid>
 </AccordionDetails>
 </Accordion>
 ))}
 </CardContent>
 </Card>
 </Grid>

 {/* Team Members */}
 <Grid item xs={12} lg={4}>
 <Card>
 <CardContent>
 <Typography variant="h6" gutterBottom>
 Team Members
 </Typography>
 
 {teamMembers.map((member) => {
 const completedTasks = member.tasks.filter(t => t.status === 'completed').length;
 const totalTasks = member.tasks.length;
 const progress = (completedTasks / totalTasks) * 100;
 
 return (
 <Card key={member.id} variant="outlined" sx={{ mb: 2, p: 2 }}>
 <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
 <Avatar sx={{ bgcolor: member.color, mr: 2 }}>
 {member.avatar}
 </Avatar>
 <Box sx={{ flex: 1 }}>
 <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
 {member.name}
 </Typography>
 <Typography variant="body2" color="text.secondary">
 {member.role}
 </Typography>
 </Box>
 <Badge 
 color={getOnlineStatus(member.id) ?"success" :"default"} 
 variant="dot"
 >
 <Box />
 </Badge>
 </Box>
 
 <Box sx={{ mb: 1 }}>
 <Typography variant="body2" color="text.secondary">
 Progress: {completedTasks}/{totalTasks} tasks
 </Typography>
 <LinearProgress 
 variant="determinate" 
 value={progress} 
 sx={{ height: 6, borderRadius: 3, mt: 0.5 }}
 />
 </Box>
 
 <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
 {member.responsibilities.map((resp, index) => (
 <Chip 
 key={index} 
 label={resp} 
 size="small" 
 variant="outlined"
 sx={{ fontSize: '0.7rem' }}
 />
 ))}
 </Box>
 
 <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
 {member.icon}
 <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
 {member.tasks.filter(t => t.status === 'in_progress').length} active tasks
 </Typography>
 </Box>
 </Card>
 );
 })}
 </CardContent>
 </Card>
 </Grid>
 </Grid>

 {/* Chat Dialog */}
 <Dialog 
 open={chatOpen} 
 onClose={() => setChatOpen(false)}
 maxWidth="md"
 fullWidth
 >
 <DialogTitle>
 <Box sx={{ display: 'flex', alignItems: 'center' }}>
 <Group sx={{ mr: 1 }} />
 Team Chat Room - {selectedProject?.title}
 </Box>
 </DialogTitle>
 
 <DialogContent sx={{ height: '500px', p: 0 }}>
 <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
 {/* Chat Messages */}
 <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
 {chatMessages.map((message) => {
 const sender = teamMembers.find(r => r.id === message.senderId);
 return (
 <Box key={message.id} sx={{ mb: 2, display: 'flex', alignItems: 'flex-start' }}>
 <Avatar 
 sx={{ 
 bgcolor: sender?.color || '#9E9E9E',
 width: 32,
 height: 32,
 mr: 1,
 mt: 0.5
 }}
 >
 {sender?.avatar || '?'}
 </Avatar>
 <Box sx={{ flex: 1 }}>
 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
 <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
 {message.senderName}
 </Typography>
 <Typography variant="caption" color="text.secondary">
 {formatTime(message.timestamp)}
 </Typography>
 {getOnlineStatus(message.senderId) && (
 <Badge color="success" variant="dot" />
 )}
 </Box>
 <Typography variant="body2" sx={{ 
 bgcolor: 'grey.100', 
 p: 1, 
 borderRadius: 1,
 wordBreak: 'break-word'
 }}>
 {message.message}
 </Typography>
 </Box>
 </Box>
 );
 })}
 </Box>
 
 {/* Chat Input */}
 <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
 <Box sx={{ display: 'flex', gap: 1 }}>
 <TextField
 fullWidth
 placeholder="Type your message..."
 value={newMessage}
 onChange={(e) => setNewMessage(e.target.value)}
 onKeyPress={(e) => {
 if (e.key === 'Enter') {
 handleSendMessage();
 }
 }}
 size="small"
 />
 <Button 
 variant="contained" 
 onClick={handleSendMessage}
 disabled={!newMessage.trim()}
 >
 Send
 </Button>
 </Box>
 </Box>
 </Paper>
 </DialogContent>
 
 <DialogActions>
 <Button onClick={() => setChatOpen(false)}>Close</Button>
 </DialogActions>
 </Dialog>
 </Box>
 );
};

export default TeamRoleReplica;



